import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, normalizeStyle as _normalizeStyle, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-32235550"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "email-items-header-container" }
const _hoisted_2 = { class: "button-container" }
const _hoisted_3 = ["onMouseover"]
const _hoisted_4 = ["onClick"]

import { computed, inject, ref, watch } from "vue";
import GridBase from "@/components/ui/grid/grid-base.vue";
import { DxColumn } from "devextreme-vue/data-grid";
import GridMenu from "@/components/ui/grid/grid-menu.vue";
import { ContextMenuItem } from "@/types/ui/menus/ContextMenuItem";
import { DefaultContactType } from "acquaint-api/constants/DefaultContactType";
import FindContactModal from "@/components/find/contacts/find-contact-modal.vue";
import { PermittedOccupierItem } from "@/types/tenancies/PermittedOccupierItem";
import { ContactFindResult } from "@/types/find/contacts/ContactFindResult";
import { ContactStatus } from "@/enums/contacts/ContactStatus";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import displayErrorMessage from "@/utils/ErrorHandling";
import { getErrorMessage } from "bright-logic-core/src/errors";
import ContactsPeekPopup from "@/components/contacts/contacts-peek-popup.vue";
import ContactModal from "@/components/contacts/record/contact-modal.vue";
import { Contacts } from "acquaint-api/models/Contacts";
import moment from "moment";
import { ContactHelper } from "@/utils/contacts/ContactHelper";
import { useContactHelper } from "@/utils/contacts/useContactHelper";


export default /*#__PURE__*/_defineComponent({
  __name: 'permitted-occupiers',
  props: {
  /**
   * A numeric value used to force updates or re-renders in the component.
   * @type {Number}
   * @required
   */
  forceUpdateValue: {
    type: Number,
    required: true,
  },
  /**
   * Flag to indicate if the initial form data is permitted for use.
   * @type {Boolean}
   * @required
   */
  initialPermittedFormData: {
    type: Boolean,
    required: true,
  },
  /**
   * The height of grid.
   * @type {String}
   * @default 80vh
   */
  gridHeight: {
    type: String,
    default: "45vh",
  },
  /**
   * The boolean value to decide whether the fields are ready-only.
   * @type {boolean}
   */
  readOnly: {
    type: Boolean,
    default: false,
  },
  /** Id of the tenancy
   * @type {number}
   * @default: 0
   */
  tenancyId: {
    type: Number,
    default: 0,
  },
  /**
   * The boolean value to decide whether it's for edit.
   * @type {boolean}
   */
  editMode: {
    type: Boolean,
    default: false,
  },
},
  emits: [
  /**
   * Triggered when the component is initially opened.
   */
  "initialOpened",
  /**
   * Triggered when the contactIds are successfully updated.
   */
  "updateContactIds",
  /**
   * Triggered when the user clicks to navigate to the selected contact's details page.
   */
  "navigateToContact",
],
  setup(__props, { emit }) {

const props = __props;





const { fetchContactById } = useContactHelper();
const acquaintApiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const contextMenuId = ref("permitted-occupiers-grid-items");
const permittedOccupierItems = ref<PermittedOccupierItem[]>([]);
const selectedRecord = ref<PermittedOccupierItem>();
const defaultContactType = ref(-1);
const contactIconId = ref(0);
const loading = ref(false);
const displayFindContactModal = ref(false);
const contactIds = ref<number[]>([]);
const peekContactId = ref(0);
const peekModalKey = ref(0);
const showPeek = ref(false);
const peekContactName = ref("");
const displayNewContactModal = ref(false);

const gridHeight = computed(() => {
  return `height: ${props.gridHeight};`;
});

const fetchPermittedOccupierItems = async (tenancyId: number) => {
  if (tenancyId <= 0) return;
  try {
    const items = await acquaintApiClient.httpGet<PermittedOccupierItem[]>(
      `app/tenancy/${tenancyId}/permittedoccupiers`,
    );
    if (items) {
      permittedOccupierItems.value = [...items];
    }
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    permittedOccupierItems.value = [];
  }
};

watch(
  () => props.forceUpdateValue,
  async () => {
    loading.value = true;
    selectedRecord.value = undefined;
    contextMenuId.value = `${contextMenuId.value}-${moment().unix()}`;
    if (props.initialPermittedFormData) {
      permittedOccupierItems.value = [];
    }
    if (props.tenancyId > 0) {
      await fetchPermittedOccupierItems(props.tenancyId);
    }
    contactIds.value = [];
    loading.value = false;
    emit("initialOpened");
  },
  { immediate: true },
);

const menuItems = computed(() => {
  const menu: ContextMenuItem[] = [
    {
      key: "add",
      text: "Add",
      icon: "fas fa-user",
    },
    {
      key: "remove",
      text: "Remove",
      icon: "fas fa-trash",
      disabled: !selectedRecord.value,
    },
  ];
  return menu;
});

const onMenuItemClicked = async (key: string) => {
  switch (key) {
    case "add":
      defaultContactType.value = DefaultContactType.PermittedOccupier;
      displayFindContactModal.value = true;
      break;
    case "remove":
      removeContact();
      break;
    default:
      break;
  }
};

const onRowSelected = (e: PermittedOccupierItem | undefined) => {
  if (!e) {
    selectedRecord.value = undefined;
    return;
  }
  selectedRecord.value = e;
};

const navigateToContact = (contact: PermittedOccupierItem) => {
  if (contact.contactId > 0) {
    peekModalKey.value++;
    showPeek.value = true;
    peekContactId.value = contact.contactId;
    peekContactName.value = contact.contactName;
  }
};

const displayContactIcon = (id: number) => {
  contactIconId.value = id;
};

const hideContactIcon = () => {
  contactIconId.value = 0;
};

const handleContactSelected = async (selectedContact: ContactFindResult) => {
  if (!selectedContact) {
    return;
  }

  const contactId = selectedContact.id;
  if (!contactIds.value.includes(contactId)) {
    contactIds.value.push(contactId);
    addPermittedOccupier({
      contactId: selectedContact.id,
      contactName: selectedContact.name,
    });
  }
  collectPermittedOccupiers();
};

const addPermittedOccupier = (newItem: PermittedOccupierItem) => {
  const exists = permittedOccupierItems.value.some(
    (item) => item.contactId === newItem.contactId,
  );

  if (!exists) {
    permittedOccupierItems.value.push(newItem);
    permittedOccupierItems.value = [...permittedOccupierItems.value];
  }
};

const removeContact = () => {
  if (selectedRecord.value) {
    permittedOccupierItems.value = permittedOccupierItems.value.filter(
      (item) => item.contactId !== selectedRecord.value?.contactId,
    );
    permittedOccupierItems.value = [...permittedOccupierItems.value];
    collectPermittedOccupiers();
  }
};

const collectPermittedOccupiers = () => {
  const ids = permittedOccupierItems.value
    .map((item) => item.contactId)
    .filter((id): id is number => id > 0);

  // Make unique and assign (preserves order of first occurrence)
  contactIds.value = [...new Set(ids)];
  emit("updateContactIds", contactIds.value);
};

const openNewContactModal = () => {
  displayNewContactModal.value = true;
};

const contactCreated = async (id: number) => {
  const contact: Contacts | undefined = await fetchContactById(id);
  if (contact) {
    contactIds.value.push(contact.id);
    addPermittedOccupier({
      contactId: contact.id,
      contactName: ContactHelper.formatContactName(contact),
    });
  }
  collectPermittedOccupiers();
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        (!__props.readOnly)
          ? (_openBlock(), _createBlock(GridMenu, {
              key: 0,
              "menu-items": menuItems.value,
              onItemClicked: onMenuItemClicked,
              "button-icon": "fas fa-bars",
              id: contextMenuId.value
            }, null, 8, ["menu-items", "id"]))
          : _createCommentVNode("", true)
      ])
    ]),
    _createElementVNode("div", {
      style: _normalizeStyle(gridHeight.value)
    }, [
      _createVNode(GridBase, {
        "data-source": permittedOccupierItems.value,
        "id-column-name": "contactId",
        "column-auto-width": true,
        loading: loading.value,
        onAdaptiveRowSelection: onRowSelected,
        onRowSelected: onRowSelected,
        wordWrapEnabled: true,
        showSearchPanel: false,
        "custom-column-slots": ['contactCell']
      }, {
        columns: _withCtx(() => [
          _createVNode(_unref(DxColumn), {
            "data-field": "contactId",
            caption: "Id",
            "hiding-priority": 1,
            visible: false
          }),
          _createVNode(_unref(DxColumn), {
            "data-field": "contactName",
            caption: "Contact",
            "hiding-priority": 2,
            "cell-template": "contactCell"
          })
        ]),
        contactCellData: _withCtx(({ data }) => [
          _createElementVNode("div", {
            onMouseover: ($event: any) => (displayContactIcon(data.contactId)),
            onMouseleave: hideContactIcon
          }, [
            (contactIconId.value == data.contactId)
              ? (_openBlock(), _createElementBlock("i", {
                  key: 0,
                  onClick: ($event: any) => (navigateToContact(data)),
                  class: _normalizeClass('fas fa-eye')
                }, null, 8, _hoisted_4))
              : _createCommentVNode("", true),
            _createTextVNode(" " + _toDisplayString(data.contactName), 1)
          ], 40, _hoisted_3)
        ]),
        _: 1
      }, 8, ["data-source", "loading"])
    ], 4),
    _createVNode(FindContactModal, {
      modelValue: displayFindContactModal.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((displayFindContactModal).value = $event)),
      onContactSelected: handleContactSelected,
      typeToFind: defaultContactType.value,
      defaultStatus: _unref(ContactStatus).Current,
      showNewContactButton: true,
      onOpenNewContactModal: openNewContactModal
    }, null, 8, ["modelValue", "typeToFind", "defaultStatus"]),
    (_openBlock(), _createBlock(ContactsPeekPopup, {
      key: peekModalKey.value,
      modelValue: showPeek.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showPeek).value = $event)),
      contactId: peekContactId.value,
      title: peekContactName.value
    }, null, 8, ["modelValue", "contactId", "title"])),
    _createVNode(ContactModal, {
      modelValue: displayNewContactModal.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((displayNewContactModal).value = $event)),
      onContactCreated: contactCreated,
      defaultType: _unref(DefaultContactType).PermittedOccupier
    }, null, 8, ["modelValue", "defaultType"])
  ], 64))
}
}

})